import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { useUserActions } from "_actions";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import axaLogo from "../../../assets/images/axaLogo.png";
import imgOne from "../../../assets/images/login-page-image-one.jpg";
import imgTwo from "../../../assets/images/login-page-image-two.jpg";
import imgThree from "../../../assets/images/login-page-image-three.jpg";
import { QRCodeCanvas } from "qrcode.react";
import "./LoginComponent.css";

function LoginComponent() {
    const [loginPageImages] = useState([
        imgOne,
        imgTwo,
        imgThree,
    ]);

    const isMountedRef = useRef(true);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const [currentImageIndex] = useState(
        Math.floor(Math.random() * loginPageImages.length)
    );

    const [step, setStep] = useState("credentials");

    const [credentials, setCredentials] = useState({
        username: "",
        password: "",
    });

    const [secret, setSecret] = useState("");
    const [otpauthUrl, setOtpauthUrl] = useState("");
    const [softTokenCode, setSoftTokenCode] = useState("");

    const [loginError, setLoginError] = useState("");
    const [mfaLoading, setMfaLoading] = useState(false);

    const userActions = useUserActions();

    const validationSchema = Yup.object().shape({
        username: Yup.string().required("Username is required"),
        password: Yup.string().required("Password is required"),
    });

    const formOptions = {
        resolver: yupResolver(validationSchema),
    };

    const {
        register,
        handleSubmit,
        formState,
    } = useForm(formOptions);

    const { errors, isSubmitting } = formState;

    const baseUrl = `${process.env.REACT_APP_API_URL}/api/Users`;

    /*
     * Username + Password
     */
    const onLogin = async (data) => {
        try {
            setLoginError("");

            // MFA aşamasında kullanılacak kullanıcı bilgilerini sakla
            setCredentials({
                username: data.username,
                password: data.password,
            });

            const response = await fetch(`${baseUrl}/Authenticate`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    username: data.username,
                    password: data.password,
                    type: "Dashboard",
                }),
            });

            const result = await response.json();

            if (!response.ok) {
                setLoginError(result.message || "Giriş başarısız");
                return;
            }

            // MFA gerekiyorsa henüz JWT alma
            if (result.twoFARequired) {

                // İlk MFA kurulumu
                if (result.mfaSetupRequired) {
                    await generateMfaSetup(
                        data.username,
                        data.password
                    );
                }
                // MFA daha önce kurulmuş
                else {
                    setSoftTokenCode("");
                    setStep("mfa");
                }

                return;
            }

            // MFA gerekmiyorsa normal login
            userActions.completeLogin(result);

        } catch (error) {
            setLoginError("Sunucuya bağlanırken hata oluştu");
        }
    };

    /*
     * İlk MFA kurulumu için QR oluştur.
     */
    const generateMfaSetup = async (username, password) => {
        try {
            setMfaLoading(true);
            setLoginError("");

            const response = await fetch(`${baseUrl}/EnableMfa`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    Username: username,
                    Password: password,
                }),
            });

            const result = await response.json();

            if (!response.ok) {
                console.error("EnableMfa error:", result);

                setLoginError(
                    result?.message ||
                    result?.title ||
                    "MFA kurulumu başlatılamadı"
                );

                return;
            }

            setSecret(result.secret);
            setOtpauthUrl(result.otpauthUrl);
            setSoftTokenCode("");
            setStep("mfa-setup");
        } catch (error) {
            setLoginError("MFA kurulumu sırasında hata oluştu");
        } finally {
            if (isMountedRef.current) {
                setMfaLoading(false);
            }
        }
    };

    /*
     * İlk MFA kurulumu tamamla.
     */
    const confirmMfa = async (e) => {
        e.preventDefault();

        if (softTokenCode.length !== 6) {
            setLoginError("6 haneli MFA kodunu giriniz");
            return;
        }

        try {
            setMfaLoading(true);
            setLoginError("");

            const response = await fetch(`${baseUrl}/ConfirmMfa`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    Username: credentials.username,
                    Password: credentials.password,
                    Secret: secret,
                    Code: softTokenCode,
                }),
            });

            const result = await response.json();
            if (!response.ok) {
                console.error("ConfirmMfa error:", result);

                setLoginError(
                    result?.message ||
                    result?.title ||
                    "MFA doğrulaması başarısız"
                );

                return;
            }

            if (result.token) {
                userActions.completeLogin(result);
                return;
            }
        } catch (error) {
            setLoginError("MFA doğrulaması sırasında hata oluştu");
        } finally {
            if (isMountedRef.current) {
                setMfaLoading(false);
            }
        }
    };

    /*
     * Daha önce kurulmuş MFA'yı doğrula.
     */
    const verifyMfa = async (e) => {
        e.preventDefault();

        if (softTokenCode.length !== 6) {
            setLoginError("6 haneli MFA kodunu giriniz");
            return;
        }

        try {
            setMfaLoading(true);
            setLoginError("");

            const response = await fetch(`${baseUrl}/VerifyMfa`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    Username: credentials.username,
                    Password: credentials.password,
                    Code: softTokenCode,
                }),
            });

            const result = await response.json();

            if (!response.ok) {
                setLoginError(
                    result.message || "MFA doğrulaması başarısız"
                );
                return;
            }

            if (result.token) {
                userActions.completeLogin(result);
                return;
            }
        } catch (error) {
            setLoginError("MFA doğrulaması sırasında hata oluştu");
        } finally {
            if (isMountedRef.current) {
                setMfaLoading(false);
            }
        }
    };

    return (
        <div className="login-container">
            <div
                className="login-image-area"
                style={{
                    backgroundImage: `url(${loginPageImages[currentImageIndex]})`,
                }}
            >
                <div className="auth-page-content">
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-lg-12 login-area">
                                <div className="text-center mt-sm-5 mb-4 text-white-50">
                                    <div>
                                        <Link to="/" className="d-inline-block auth-logo">
                                            <img
                                                src={axaLogo}
                                                alt=""
                                                height="75"
                                                width="75"
                                            />
                                        </Link>
                                    </div>
                                    <p
                                        className="mt-3 fs-15 fw-medium"
                                        style={{ color: "black" }}
                                    >
                                        Axa Giriş Paneline Hoş geldiniz
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="row justify-content-center">
                            <div className="col-md-8 col-lg-6 col-xl-5">
                                <div className="mt-4">
                                    <div className="login p-4">

                                        {/* NORMAL LOGIN */}
                                        {step === "credentials" && (
                                            <>
                                                <div className="text-center mt-2">
                                                    <h5 className="text-primary">
                                                        Giriş Yap
                                                    </h5>
                                                </div>

                                                <div
                                                    className="p-2 mt-4"
                                                    style={{
                                                        display: "flex",
                                                        flexDirection: "row",
                                                        justifyContent: "center",
                                                    }}
                                                >
                                                    <form
                                                        onSubmit={handleSubmit(onLogin)}>
                                                        <div className="mb-3">
                                                            <label
                                                                htmlFor="email"
                                                                className="form-label"
                                                            >
                                                                Kullanıcı Adı
                                                            </label>

                                                            <input
                                                                name="username"
                                                                type="text"
                                                                {...register("username")}
                                                                className={`form-control ${errors.username
                                                                    ? "is-invalid"
                                                                    : ""
                                                                    }`}
                                                            />

                                                            <div className="invalid-feedback">
                                                                {errors.username?.message}
                                                            </div>
                                                        </div>

                                                        <div className="mb-3">
                                                            <label
                                                                className="form-label"
                                                                htmlFor="password-input"
                                                            >
                                                                Şifre
                                                            </label>

                                                            <div className="position-relative auth-pass-inputgroup mb-3">
                                                                <input
                                                                    name="password"
                                                                    type="password"
                                                                    {...register("password")}
                                                                    className={`input form-control ${errors.password
                                                                        ? "is-invalid"
                                                                        : ""
                                                                        }`}
                                                                />

                                                                <div className="invalid-feedback">
                                                                    {errors.password?.message}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {loginError && (
                                                            <div className="text-danger mb-3">
                                                                {loginError}
                                                            </div>
                                                        )}

                                                        <div className="mt-4">
                                                            <button
                                                                color="button success"
                                                                className="btn btn-success w-100"
                                                                type="submit"
                                                                disabled={isSubmitting}
                                                            >
                                                                {isSubmitting && (
                                                                    <span className="spinner-border spinner-border-sm mr-1"></span>
                                                                )}{" "}
                                                                Giriş Yap
                                                            </button>
                                                        </div>

                                                        <div className="mt-4 text-center"></div>
                                                    </form>
                                                </div>
                                            </>
                                        )}

                                        {/* MFA SETUP */}
                                        {step === "mfa-setup" && (
                                            <div className="text-center">

                                                <h5 className="text-primary">
                                                    MFA Kurulumu
                                                </h5>

                                                <p className="mt-3">
                                                    Google Authenticator uygulamasını açın
                                                    ve aşağıdaki QR kodu okutun.
                                                </p>

                                                {otpauthUrl && (
                                                    <div className="mt-3 mb-3">
                                                        <QRCodeCanvas
                                                            value={otpauthUrl}
                                                            size={220}
                                                        />
                                                    </div>
                                                )}

                                                <p>
                                                    Uygulamadaki 6 haneli kodu giriniz.
                                                </p>

                                                <form onSubmit={confirmMfa}>
                                                    <input
                                                        type="text"
                                                        className="form-control text-center"
                                                        maxLength={6}
                                                        inputMode="numeric"
                                                        value={softTokenCode}
                                                        onChange={(e) =>
                                                            setSoftTokenCode(
                                                                e.target.value.replace(/\D/g, "")
                                                            )
                                                        }
                                                        placeholder="6 haneli kod"
                                                    />

                                                    {loginError && (
                                                        <div className="text-danger mt-3">
                                                            {loginError}
                                                        </div>
                                                    )}

                                                    <button
                                                        type="submit"
                                                        className="btn btn-success w-100 mt-4"
                                                        disabled={mfaLoading}
                                                    >
                                                        {mfaLoading && (
                                                            <span className="spinner-border spinner-border-sm mr-1"></span>
                                                        )}{" "}
                                                        MFA'yı Etkinleştir ve Giriş Yap
                                                    </button>
                                                </form>

                                            </div>
                                        )}

                                        {/* NORMAL MFA */}
                                        {step === "mfa" && (
                                            <div className="text-center">

                                                <h5 className="text-primary">
                                                    MFA Doğrulama
                                                </h5>

                                                <p className="mt-3">
                                                    Google Authenticator uygulamanızdaki
                                                    6 haneli kodu giriniz.
                                                </p>

                                                <form onSubmit={verifyMfa}>
                                                    <input
                                                        type="text"
                                                        className="form-control text-center"
                                                        maxLength={6}
                                                        inputMode="numeric"
                                                        value={softTokenCode}
                                                        onChange={(e) =>
                                                            setSoftTokenCode(
                                                                e.target.value.replace(/\D/g, "")
                                                            )
                                                        }
                                                        placeholder="6 haneli kod"
                                                    />

                                                    {loginError && (
                                                        <div className="text-danger mt-3">
                                                            {loginError}
                                                        </div>
                                                    )}

                                                    <button
                                                        type="submit"
                                                        className="btn btn-success w-100 mt-4"
                                                        disabled={mfaLoading}
                                                    >
                                                        {mfaLoading && (
                                                            <span className="spinner-border spinner-border-sm mr-1"></span>
                                                        )}{" "}
                                                        Doğrula ve Giriş Yap
                                                    </button>
                                                </form>

                                            </div>
                                        )}

                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
}

export { LoginComponent };